import {
    Box,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Divider,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import CloseSmallIcon from "@mui/icons-material/CloseOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { 
    GoogleMap, 
    MarkerF,
    Circle,
} from '@react-google-maps/api';
import _ from "lodash";
import { When } from "react-if";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { DeviceTypes } from "../../types";
import { DeviceService } from "../../services/device.service";
import { LatLng } from "../event-map";
import { markerConstants, markerRadiusColorConstants } from "../../constants/marker.constants";

const RETURN_STATUS_OPTIONS = [
    { value: 'not_returned', label: 'Not Returned' },
    { value: 'returned', label: 'Returned' },
    { value: 'in_transit', label: 'In Transit' },
];

const getReturnStatusLabel = (value: string | null | undefined): string => {
    return RETURN_STATUS_OPTIONS.find(o => o.value === value)?.label ?? 'Not Set';
};

interface Props {
    onClose(): void,
    onUpdate?(): void,
    visible: boolean,
    device: DeviceTypes.Model,
    assetManagementEnabled?: boolean,
}

export const DeviceDetailsDrawer: React.FC<Props> = ({
    onClose,
    onUpdate,
    visible,
    device,
    assetManagementEnabled = false,
}) => {
    const mapRef = useRef<google.maps.Map | null>(null);
    const isMountedRef = useRef<boolean>(false);

    const [editingAssetTag, setEditingAssetTag] = useState(false);
    const [assetTagValue, setAssetTagValue] = useState(device?.assetTag ?? '');
    const [savingAssetTag, setSavingAssetTag] = useState(false);
    const [showSavedCheck, setShowSavedCheck] = useState(false);
    const [confirmingRemove, setConfirmingRemove] = useState(false);

    const [displayAssetTag, setDisplayAssetTag] = useState(device?.assetTag ?? '');

    // Notes state
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesValue, setNotesValue] = useState(device?.notes ?? '');
    const [displayNotes, setDisplayNotes] = useState(device?.notes ?? '');
    const [savingNotes, setSavingNotes] = useState(false);
    const [showNotesSavedCheck, setShowNotesSavedCheck] = useState(false);

    // Return Status state
    const [returnStatusValue, setReturnStatusValue] = useState(device?.returnStatus ?? '');
    const [displayReturnStatus, setDisplayReturnStatus] = useState(device?.returnStatus ?? '');
    const [editingReturnStatus, setEditingReturnStatus] = useState(false);
    const [savingReturnStatus, setSavingReturnStatus] = useState(false);
    const [showReturnStatusSavedCheck, setShowReturnStatusSavedCheck] = useState(false);

    const handleSaveAssetTag = async () => {
        // If clearing an existing tag, ask for confirmation first
        if (displayAssetTag && !assetTagValue) {
            setConfirmingRemove(true);
            return;
        }
        await performSave();
    };

    const performSave = async () => {
        setConfirmingRemove(false);
        setSavingAssetTag(true);
        await DeviceService.updateAssetTag(
            device.id,
            assetTagValue || null,
            (isValid, msg) => {
                setSavingAssetTag(false);
                if (isValid) {
                    setDisplayAssetTag(assetTagValue);
                    setEditingAssetTag(false);
                    setShowSavedCheck(true);
                    setTimeout(() => setShowSavedCheck(false), 2000);
                    onUpdate?.();
                } else {
                    toast(msg || 'Failed to update asset tag', { autoClose: 1500, type: 'error' });
                }
            },
        );
    };

    const handleSaveNotes = async () => {
        setSavingNotes(true);
        await DeviceService.updateDeviceNotes(
            device.id,
            notesValue || null,
            (isValid, msg) => {
                setSavingNotes(false);
                if (isValid) {
                    setDisplayNotes(notesValue);
                    setEditingNotes(false);
                    setShowNotesSavedCheck(true);
                    setTimeout(() => setShowNotesSavedCheck(false), 2000);
                    onUpdate?.();
                } else {
                    toast(msg || 'Failed to update notes', { autoClose: 1500, type: 'error' });
                }
            },
        );
    };

    const handleSaveReturnStatus = async (newValue: string) => {
        setSavingReturnStatus(true);
        setReturnStatusValue(newValue);
        await DeviceService.updateDeviceReturnStatus(
            device.id,
            newValue || null,
            (isValid, msg) => {
                setSavingReturnStatus(false);
                if (isValid) {
                    setDisplayReturnStatus(newValue);
                    setEditingReturnStatus(false);
                    setShowReturnStatusSavedCheck(true);
                    setTimeout(() => setShowReturnStatusSavedCheck(false), 2000);
                    onUpdate?.();
                } else {
                    toast(msg || 'Failed to update return status', { autoClose: 1500, type: 'error' });
                }
            },
        );
    };

    const [center, setCenter] = useState<LatLng>(
        {
            lat: Number(device?.lastLocation?.lat),
            lng: Number(device?.lastLocation?.lng)
        }
    );
    const [zoom, setZoom] = useState<number>(18);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    return (
        <Drawer
            anchor="bottom"
            open={visible}
            onClose={onClose}
        >
            <Box
                sx={{ minHeight: { xs: "500px", md: "500px" } }}
            >
                <Stack direction={'row'} justifyContent={'space-between'} mt={1} style={{ padding: 10 }}>
                    <Typography fontSize={24} fontWeight={400} textAlign={'left'}>
                        Device Details
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>

                <Divider />

                <Box style={{ padding: 16 }} mt={1}>
                    <Grid container spacing={2}>
                        <Grid container spacing={2} p={2} md={4} sm={12} alignContent={'flex-start'}>
                            <Grid item md={12} sm={12} xs={12}>
                                <Typography mb={2} fontWeight={400}>
                                    Information About The Device
                                </Typography>
                            </Grid>

                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Name:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.name}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Serial Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.serialNumber}
                                </Typography>
                            </Grid>
                            {assetManagementEnabled && <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Asset Tag:
                                </Typography>
                                {confirmingRemove ? (
                                    <Stack spacing={0.5} mt={0.5}>
                                        <Typography fontSize={13} sx={{ color: 'warning.main' }}>
                                            Remove asset tag?
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <IconButton size="small" onClick={performSave} color="primary">
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => { setConfirmingRemove(false); setAssetTagValue(displayAssetTag || ''); setEditingAssetTag(false); }}>
                                                <CloseSmallIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                ) : editingAssetTag ? (
                                    <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                                        <TextField
                                            size="small"
                                            value={assetTagValue}
                                            onChange={(e) => setAssetTagValue(e.target.value.slice(0, 50))}
                                            placeholder="Enter asset tag"
                                            disabled={savingAssetTag}
                                            inputProps={{ maxLength: 50 }}
                                            sx={{ flex: 1 }}
                                            autoFocus
                                        />
                                        <IconButton size="small" onClick={handleSaveAssetTag} disabled={savingAssetTag} color="primary">
                                            <CheckIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => { setEditingAssetTag(false); setAssetTagValue(displayAssetTag || ''); }}>
                                            <CloseSmallIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                                        {displayAssetTag ? (
                                            <>
                                                {showSavedCheck ? (
                                                    <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                                ) : null}
                                                <Typography fontSize={14} fontWeight={400}>
                                                    {displayAssetTag}
                                                </Typography>
                                                <IconButton size="small" onClick={() => setEditingAssetTag(true)}>
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                onClick={() => setEditingAssetTag(true)}
                                                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                                            >
                                                {showSavedCheck ? (
                                                    <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                                ) : (
                                                    <ErrorOutlineIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                                                )}
                                                <Typography fontSize={13} sx={{ color: 'text.secondary', textDecoration: 'underline' }}>
                                                    {showSavedCheck ? 'Tag removed' : 'Assign asset tag'}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                )}
                            </Grid>}
                            {assetManagementEnabled && !device?.userId && (
                                <Grid item md={6} sm={12} xs={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Return Status:
                                    </Typography>
                                    {editingReturnStatus ? (
                                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                                            <FormControl size="small" sx={{ flex: 1 }}>
                                                <Select
                                                    value={returnStatusValue}
                                                    onChange={(e) => handleSaveReturnStatus(e.target.value)}
                                                    disabled={savingReturnStatus}
                                                    displayEmpty
                                                >
                                                    <MenuItem value="">
                                                        <em>Not Set</em>
                                                    </MenuItem>
                                                    {RETURN_STATUS_OPTIONS.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <IconButton size="small" onClick={() => { setEditingReturnStatus(false); setReturnStatusValue(displayReturnStatus); }}>
                                                <CloseSmallIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" alignItems="center" spacing={0.5} mt={1}>
                                            {showReturnStatusSavedCheck && (
                                                <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                            )}
                                            <Typography fontSize={14} fontWeight={400}>
                                                {getReturnStatusLabel(displayReturnStatus)}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setEditingReturnStatus(true)}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Stack>
                                    )}
                                </Grid>
                            )}
                            {assetManagementEnabled && !device?.userId && (
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography fontSize={14} fontWeight={500}>
                                        Notes:
                                    </Typography>
                                    {editingNotes ? (
                                        <Stack spacing={0.5} mt={0.5}>
                                            <TextField
                                                size="small"
                                                value={notesValue}
                                                onChange={(e) => setNotesValue(e.target.value.slice(0, 500))}
                                                placeholder="Enter notes about this device"
                                                disabled={savingNotes}
                                                inputProps={{ maxLength: 500 }}
                                                multiline
                                                rows={3}
                                                fullWidth
                                                autoFocus
                                            />
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={0.5}>
                                                    <IconButton size="small" onClick={handleSaveNotes} disabled={savingNotes} color="primary">
                                                        <CheckIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => { setEditingNotes(false); setNotesValue(displayNotes); }}>
                                                        <CloseSmallIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                                <Typography fontSize={11} sx={{ color: 'text.disabled' }}>
                                                    {notesValue.length}/500
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" alignItems="flex-start" spacing={0.5} mt={1}>
                                            {showNotesSavedCheck && (
                                                <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                            )}
                                            <Typography fontSize={14} fontWeight={400} sx={{ whiteSpace: 'pre-wrap', color: displayNotes ? 'text.primary' : 'text.disabled' }}>
                                                {displayNotes || 'No notes'}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setEditingNotes(true)}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Stack>
                                    )}
                                </Grid>
                            )}
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Mac Id:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.macId}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Default Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.deviceConfig?.defaultNumber || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Primary Emergency Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.deviceConfig?.primaryEmergencyNumber || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Secondary Emergency Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.deviceConfig?.secondaryEmergencyNumber || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Secondary Friend Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.deviceConfig?.secondaryFriendNumber || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Battery Level:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.batteryLevel || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={12} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Heart Beat Frequency Seconds:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.deviceConfig?.heartbeatFrequencySeconds || 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider orientation="vertical" flexItem />

                        <Grid container spacing={2} p={2} md={4} sm={12} xs={12} alignContent={'flex-start'}>
                            <Grid item md={12} sm={12} xs={12}>
                                <Typography mb={2} fontWeight={400}>
                                    User Information
                                </Typography>
                            </Grid>

                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    First Name:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.firstName}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Last Name:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.lastName}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Phone Number:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.phoneNumber}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Backup Email:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.backupEmail || 'N/A'}
                                </Typography>
                            </Grid>

                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    Blood Type:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.bloodType || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    User Weight:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.weight || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item md={6} sm={12} xs={12}>
                                <Typography fontSize={14} fontWeight={500}>
                                    User Height:
                                </Typography>
                                <Typography fontSize={14} fontWeight={400} mt={1}>
                                    {device?.user?.height || 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Divider orientation="vertical" flexItem />

                        <Grid container spacing={2} p={2} md={4} sm={12} xs={12} alignContent={'flex-start'}>
                            <When condition={device?.lastLocation?.lat && device?.lastLocation?.lng}>
                                <Grid item md={12} sm={12} xs={12}>
                                    <Typography mb={2} fontWeight={400  }>Last Location</Typography>
                                    <div style={{ width: '100%', height: 400 }}>
                                            <GoogleMap
                                                onLoad={handleMapLoad}
                                                options={{
                                                    disableDefaultUI: true,
                                                    zoomControl: true,
                                                    fullscreenControl: true,
                                                    mapTypeControl: true,
                                                    streetViewControl: true,
                                                    scaleControl: true,
                                                    rotateControl: true,
                                                    clickableIcons: false,
                                                    scrollwheel: true,
                                                    draggable: true,
                                                    draggableCursor: 'default',
                                                    draggingCursor: 'pointer',
                                                    keyboardShortcuts: true,
                                                    disableDoubleClickZoom: false,
                                                    noClear: false,
                                                    styles: [
                                                        {
                                                            featureType: 'poi',
                                                            elementType: 'labels',
                                                            stylers: [
                                                                {
                                                                    visibility: 'off',
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                }}
                                                mapContainerStyle={{ height: '100%', width: '100%' }}
                                                zoom={zoom}
                                                center={center}
                                            >
                                                <MarkerF
                                                    position={{
                                                        lat: Number(device?.lastLocation?.lat),
                                                        lng: Number(device?.lastLocation?.lng)
                                                    }}
                                                    options={{
                                                        icon: {
                                                            url: markerConstants['device'],
                                                            scaledSize: new google.maps.Size(10, 10),
                                                            scale: 1,
                                                            origin: new google.maps.Point(0, 0),
                                                        }
                                                    }}
                                                />
                                                {(!device?.isPrecise && zoom >= 15) &&
                                                    <Circle
                                                        center={{
                                                            lat: Number(device?.lastLocation?.lat),
                                                            lng: Number(device?.lastLocation?.lng),
                                                        }}
                                                        radius={device?.appxDistMeters}
                                                        options={{
                                                            strokeColor: markerRadiusColorConstants['device'],
                                                            strokeWeight: 0.1,
                                                            fillOpacity: 0.2,
                                                            fillColor: markerRadiusColorConstants['device'],
                                                            center: {
                                                                lat: Number(device?.lastLocation?.lat),
                                                                lng: Number(device?.lastLocation?.lng),
                                                            }
                                                        }}
                                                    />
                                                }
                                            </GoogleMap>
                                    </div>
                                </Grid>
                            </When>
                        </Grid>

                    </Grid>
                </Box>
            </Box>
        </Drawer>
    )
}